/**
 * Marketplace Schema
 * Tables for the component marketplace: providers, offerings (components),
 * transactions, reviews, and categories
 */

import {
  pgTable,
  uuid,
  varchar,
  text,
  timestamp,
  integer,
  boolean,
  numeric,
  jsonb,
  index,
} from 'drizzle-orm/pg-core';
import { relations } from 'drizzle-orm';
import { profiles } from './profiles';

// =============================================================================
// Marketplace Providers
// =============================================================================
export const marketplaceProviders = pgTable('marketplace_providers', {
  id: uuid('id').primaryKey().defaultRandom(),
  userId: uuid('user_id').notNull().unique(),
  businessName: varchar('business_name', { length: 255 }).notNull(),
  businessType: varchar('business_type', { length: 50 }).notNull().default('individual'), // 'individual' | 'company' | 'organization'
  businessEmail: varchar('business_email', { length: 255 }).notNull(),
  businessPhone: varchar('business_phone', { length: 50 }),
  businessAddress: jsonb('business_address'), // { street, city, state, country, postal_code }
  taxId: varchar('tax_id', { length: 100 }),
  websiteUrl: text('website_url'),
  description: text('description'),
  logoUrl: text('logo_url'),
  verificationStatus: varchar('verification_status', { length: 50 }).notNull().default('pending'), // 'pending' | 'verified' | 'suspended' | 'rejected'
  verificationDocuments: jsonb('verification_documents').default([]),
  verificationNotes: text('verification_notes'),
  verifiedAt: timestamp('verified_at'),
  verifiedBy: uuid('verified_by'),
  payoutSettings: jsonb('payout_settings').default({}),
  revenueSharePercentage: numeric('revenue_share_percentage', { precision: 5, scale: 2 }).default(
    '70.00'
  ),
  isActive: boolean('is_active').default(true).notNull(),
  ratingAverage: numeric('rating_average', { precision: 3, scale: 2 }).default('0.00'),
  ratingCount: integer('rating_count').default(0).notNull(),
  totalSales: integer('total_sales').default(0).notNull(),
  totalRevenue: numeric('total_revenue', { precision: 12, scale: 2 }).default('0.00'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Marketplace Categories
// =============================================================================
export const marketplaceCategories = pgTable('marketplace_categories', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: varchar('name', { length: 100 }).notNull(),
  slug: varchar('slug', { length: 100 }).notNull().unique(),
  description: text('description'),
  iconName: varchar('icon_name', { length: 50 }),
  parentId: uuid('parent_id'),
  sortOrder: integer('sort_order').default(0).notNull(),
  isActive: boolean('is_active').default(true).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Marketplace Offerings (Components)
// =============================================================================
export const marketplaceOfferings = pgTable(
  'marketplace_offerings',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    providerId: uuid('provider_id').notNull(),
    categoryId: uuid('category_id').notNull(),
    name: varchar('name', { length: 255 }).notNull(),
    slug: varchar('slug', { length: 255 }).notNull().unique(),
    shortDescription: varchar('short_description', { length: 500 }).notNull(),
    longDescription: text('long_description'),
    pricingModel: varchar('pricing_model', { length: 50 }).notNull().default('free'), // 'free' | 'one_time' | 'subscription' | 'usage_based'
    basePrice: numeric('base_price', { precision: 10, scale: 2 }).default('0.00'),
    currency: varchar('currency', { length: 3 }).default('USD').notNull(),
    subscriptionInterval: varchar('subscription_interval', { length: 20 }), // 'month' | 'year'
    usageUnit: varchar('usage_unit', { length: 50 }),
    usagePrice: numeric('usage_price', { precision: 10, scale: 4 }).default('0.00'),
    tags: jsonb('tags').default([]),
    compatibilityInfo: jsonb('compatibility_info').default({}),
    previewImages: jsonb('preview_images').default([]),
    demoUrl: text('demo_url'),
    documentationUrl: text('documentation_url'),
    githubUrl: text('github_url'),
    licenseType: varchar('license_type', { length: 100 }).default('MIT').notNull(),
    licenseTerms: text('license_terms'),
    installationInstructions: text('installation_instructions'),
    supportChannels: jsonb('support_channels').default({}),
    status: varchar('status', { length: 50 }).notNull().default('draft'), // 'draft' | 'pending_review' | 'published' | 'suspended' | 'archived'
    featured: boolean('featured').default(false).notNull(),
    downloadCount: integer('download_count').default(0).notNull(),
    purchaseCount: integer('purchase_count').default(0).notNull(),
    ratingAverage: numeric('rating_average', { precision: 3, scale: 2 }).default('0.00'),
    ratingCount: integer('rating_count').default(0).notNull(),
    lastUpdated: timestamp('last_updated').defaultNow().notNull(),
    publishedAt: timestamp('published_at'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  t => [
    index('idx_offerings_provider').on(t.providerId),
    index('idx_offerings_category').on(t.categoryId),
    index('idx_offerings_status').on(t.status),
    index('idx_offerings_status_published').on(t.status, t.publishedAt),
    index('idx_offerings_provider_created').on(t.providerId, t.createdAt),
  ]
);

// =============================================================================
// Marketplace Component Versions
// =============================================================================
export const marketplaceComponentVersions = pgTable(
  'marketplace_component_versions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    componentId: uuid('component_id').notNull(),
    versionNumber: varchar('version_number', { length: 50 }).notNull(),
    isLatest: boolean('is_latest').default(false).notNull(),
    releaseNotes: text('release_notes'),
    breakingChanges: text('breaking_changes'),
    componentFiles: jsonb('component_files').default({}),
    configSchema: jsonb('config_schema').default({}),
    dependencies: jsonb('dependencies').default([]),
    compatibilityMatrix: jsonb('compatibility_matrix').default({}),
    downloadUrl: text('download_url'),
    fileSizeBytes: integer('file_size_bytes').default(0).notNull(),
    checksum: varchar('checksum', { length: 128 }),
    securityScanStatus: varchar('security_scan_status', { length: 50 })
      .default('pending')
      .notNull(), // 'pending' | 'passed' | 'failed' | 'skipped'
    securityScanResults: jsonb('security_scan_results').default({}),
    publishedAt: timestamp('published_at'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  t => [
    index('idx_versions_component').on(t.componentId),
    index('idx_versions_component_latest').on(t.componentId, t.isLatest),
  ]
);

// =============================================================================
// Marketplace Transactions
// =============================================================================
export const marketplaceTransactions = pgTable(
  'marketplace_transactions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    buyerId: uuid('buyer_id').notNull(),
    componentId: uuid('component_id').notNull(),
    componentVersionId: uuid('component_version_id'),
    providerId: uuid('provider_id').notNull(),
    purchaseType: varchar('purchase_type', { length: 50 }).notNull().default('one_time'), // 'one_time' | 'subscription' | 'usage_based'
    paymentStatus: varchar('payment_status', { length: 50 }).notNull().default('pending'), // 'pending' | 'processing' | 'completed' | 'failed' | 'refunded' | 'cancelled'
    paymentProcessor: varchar('payment_processor', { length: 50 }).default('stripe'), // 'stripe' | 'paypal'
    paymentProcessorId: varchar('payment_processor_id', { length: 255 }),
    grossAmount: numeric('gross_amount', { precision: 10, scale: 2 }).notNull(),
    platformFee: numeric('platform_fee', { precision: 10, scale: 2 }).default('0.00'),
    providerAmount: numeric('provider_amount', { precision: 10, scale: 2 }).default('0.00'),
    currency: varchar('currency', { length: 3 }).default('USD').notNull(),
    subscriptionStartDate: timestamp('subscription_start_date'),
    subscriptionEndDate: timestamp('subscription_end_date'),
    usagePeriodStart: timestamp('usage_period_start'),
    usagePeriodEnd: timestamp('usage_period_end'),
    usageQuantity: integer('usage_quantity').default(0).notNull(),
    licenseKey: varchar('license_key', { length: 255 }),
    downloadToken: varchar('download_token', { length: 255 }),
    downloadExpiresAt: timestamp('download_expires_at'),
    refundReason: text('refund_reason'),
    refundAmount: numeric('refund_amount', { precision: 10, scale: 2 }).default('0.00'),
    refundedAt: timestamp('refunded_at'),
    metadata: jsonb('metadata').default({}),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  t => [
    index('idx_transactions_buyer').on(t.buyerId),
    index('idx_transactions_provider').on(t.providerId),
    index('idx_transactions_component').on(t.componentId),
    index('idx_transactions_status').on(t.paymentStatus),
    index('idx_transactions_processor_id').on(t.paymentProcessorId),
    index('idx_transactions_provider_created').on(t.providerId, t.createdAt),
  ]
);

// =============================================================================
// Marketplace Reviews
// =============================================================================
export const marketplaceReviews = pgTable(
  'marketplace_reviews',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    componentId: uuid('component_id').notNull(),
    purchaseId: uuid('purchase_id').notNull(),
    reviewerId: uuid('reviewer_id').notNull(),
    rating: integer('rating').notNull(), // 1-5
    title: varchar('title', { length: 255 }).notNull(),
    content: text('content'),
    pros: text('pros'),
    cons: text('cons'),
    wouldRecommend: boolean('would_recommend'),
    verifiedPurchase: boolean('verified_purchase').default(true).notNull(),
    moderationStatus: varchar('moderation_status', { length: 50 }).default('pending').notNull(), // 'pending' | 'approved' | 'rejected' | 'flagged'
    moderationNotes: text('moderation_notes'),
    moderatedBy: uuid('moderated_by'),
    moderatedAt: timestamp('moderated_at'),
    helpfulVotes: integer('helpful_votes').default(0).notNull(),
    totalVotes: integer('total_votes').default(0).notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  t => [
    index('idx_reviews_component').on(t.componentId),
    index('idx_reviews_reviewer').on(t.reviewerId),
    index('idx_reviews_moderation').on(t.moderationStatus),
  ]
);

// =============================================================================
// Marketplace Review Votes
// =============================================================================
export const marketplaceReviewVotes = pgTable('marketplace_review_votes', {
  id: uuid('id').primaryKey().defaultRandom(),
  reviewId: uuid('review_id').notNull(),
  voterId: uuid('voter_id').notNull(),
  isHelpful: boolean('is_helpful').notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// =============================================================================
// Marketplace Payouts
// =============================================================================
export const marketplacePayouts = pgTable('marketplace_payouts', {
  id: uuid('id').primaryKey().defaultRandom(),
  providerId: uuid('provider_id').notNull(),
  payoutPeriodStart: timestamp('payout_period_start').notNull(),
  payoutPeriodEnd: timestamp('payout_period_end').notNull(),
  totalSalesAmount: numeric('total_sales_amount', { precision: 12, scale: 2 }).notNull(),
  platformFeesAmount: numeric('platform_fees_amount', { precision: 12, scale: 2 }).notNull(),
  payoutAmount: numeric('payout_amount', { precision: 12, scale: 2 }).notNull(),
  payoutMethod: varchar('payout_method', { length: 50 }).notNull(), // 'stripe' | 'paypal' | 'bank_transfer'
  payoutStatus: varchar('payout_status', { length: 50 }).default('pending').notNull(), // 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled'
  payoutProcessorId: varchar('payout_processor_id', { length: 255 }),
  payoutReference: varchar('payout_reference', { length: 255 }),
  payoutFailureReason: text('payout_failure_reason'),
  processedAt: timestamp('processed_at'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Relations
// =============================================================================

export const marketplaceProvidersRelations = relations(marketplaceProviders, ({ one, many }) => ({
  user: one(profiles, {
    fields: [marketplaceProviders.userId],
    references: [profiles.id],
  }),
  offerings: many(marketplaceOfferings),
  transactions: many(marketplaceTransactions, { relationName: 'providerTransactions' }),
  payouts: many(marketplacePayouts),
}));

export const marketplaceCategoriesRelations = relations(marketplaceCategories, ({ one, many }) => ({
  parent: one(marketplaceCategories, {
    fields: [marketplaceCategories.parentId],
    references: [marketplaceCategories.id],
    relationName: 'categoryHierarchy',
  }),
  children: many(marketplaceCategories, { relationName: 'categoryHierarchy' }),
  offerings: many(marketplaceOfferings),
}));

export const marketplaceOfferingsRelations = relations(marketplaceOfferings, ({ one, many }) => ({
  provider: one(marketplaceProviders, {
    fields: [marketplaceOfferings.providerId],
    references: [marketplaceProviders.id],
  }),
  category: one(marketplaceCategories, {
    fields: [marketplaceOfferings.categoryId],
    references: [marketplaceCategories.id],
  }),
  versions: many(marketplaceComponentVersions),
  transactions: many(marketplaceTransactions),
  reviews: many(marketplaceReviews),
}));

export const marketplaceComponentVersionsRelations = relations(
  marketplaceComponentVersions,
  ({ one }) => ({
    component: one(marketplaceOfferings, {
      fields: [marketplaceComponentVersions.componentId],
      references: [marketplaceOfferings.id],
    }),
  })
);

export const marketplaceTransactionsRelations = relations(marketplaceTransactions, ({ one }) => ({
  buyer: one(profiles, {
    fields: [marketplaceTransactions.buyerId],
    references: [profiles.id],
  }),
  component: one(marketplaceOfferings, {
    fields: [marketplaceTransactions.componentId],
    references: [marketplaceOfferings.id],
  }),
  componentVersion: one(marketplaceComponentVersions, {
    fields: [marketplaceTransactions.componentVersionId],
    references: [marketplaceComponentVersions.id],
  }),
  provider: one(marketplaceProviders, {
    fields: [marketplaceTransactions.providerId],
    references: [marketplaceProviders.id],
    relationName: 'providerTransactions',
  }),
  review: one(marketplaceReviews),
}));

export const marketplaceReviewsRelations = relations(marketplaceReviews, ({ one, many }) => ({
  component: one(marketplaceOfferings, {
    fields: [marketplaceReviews.componentId],
    references: [marketplaceOfferings.id],
  }),
  purchase: one(marketplaceTransactions, {
    fields: [marketplaceReviews.purchaseId],
    references: [marketplaceTransactions.id],
  }),
  reviewer: one(profiles, {
    fields: [marketplaceReviews.reviewerId],
    references: [profiles.id],
  }),
  votes: many(marketplaceReviewVotes),
}));

export const marketplaceReviewVotesRelations = relations(marketplaceReviewVotes, ({ one }) => ({
  review: one(marketplaceReviews, {
    fields: [marketplaceReviewVotes.reviewId],
    references: [marketplaceReviews.id],
  }),
  voter: one(profiles, {
    fields: [marketplaceReviewVotes.voterId],
    references: [profiles.id],
  }),
}));

export const marketplacePayoutsRelations = relations(marketplacePayouts, ({ one }) => ({
  provider: one(marketplaceProviders, {
    fields: [marketplacePayouts.providerId],
    references: [marketplaceProviders.id],
  }),
}));

// =============================================================================
// Types
// =============================================================================

export type MarketplaceProviderRecord = typeof marketplaceProviders.$inferSelect;
export type NewMarketplaceProviderRecord = typeof marketplaceProviders.$inferInsert;

export type MarketplaceCategoryRecord = typeof marketplaceCategories.$inferSelect;
export type NewMarketplaceCategoryRecord = typeof marketplaceCategories.$inferInsert;

export type MarketplaceOfferingRecord = typeof marketplaceOfferings.$inferSelect;
export type NewMarketplaceOfferingRecord = typeof marketplaceOfferings.$inferInsert;

export type MarketplaceComponentVersionRecord = typeof marketplaceComponentVersions.$inferSelect;
export type NewMarketplaceComponentVersionRecord = typeof marketplaceComponentVersions.$inferInsert;

export type MarketplaceTransactionRecord = typeof marketplaceTransactions.$inferSelect;
export type NewMarketplaceTransactionRecord = typeof marketplaceTransactions.$inferInsert;

export type MarketplaceReviewRecord = typeof marketplaceReviews.$inferSelect;
export type NewMarketplaceReviewRecord = typeof marketplaceReviews.$inferInsert;

export type MarketplaceReviewVoteRecord = typeof marketplaceReviewVotes.$inferSelect;
export type NewMarketplaceReviewVoteRecord = typeof marketplaceReviewVotes.$inferInsert;

export type MarketplacePayoutRecord = typeof marketplacePayouts.$inferSelect;
export type NewMarketplacePayoutRecord = typeof marketplacePayouts.$inferInsert;
